import { useState, useEffect, useCallback } from 'react'
import { supabase, signIn, registerOrg, signOut } from '../lib/supabase'

// Invitation code is validated server-side in the register-org Edge Function

// `startInMfa` is set by App when a session exists but has not cleared its
// second factor. Login then opens straight on the code screen instead of
// asking for a password that has already been accepted.
export default function Login({ onLogin, startInMfa = false }) {
  const [mode, setMode] = useState(startInMfa ? 'mfa' : 'login') // 'login' | 'register' | 'mfa'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [orgName, setOrgName] = useState('')
  const [fullName, setFullName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // MFA state
  const [mfaCode, setMfaCode] = useState('')
  const [mfaFactorId, setMfaFactorId] = useState('')
  const [mfaChallengeId, setMfaChallengeId] = useState('')

  // Creating a challenge is the same work whether we arrive here from a fresh
  // password entry or by resuming a half-authenticated session.
  const beginChallenge = useCallback(async () => {
    const { data: factors, error: listError } = await supabase.auth.mfa.listFactors()
    if (listError) return { error: listError }
    const totpFactor = factors?.totp?.find(f => f.status === 'verified') || factors?.totp?.[0]
    if (!totpFactor) return { error: null, factor: null }
    const { data: challengeData, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId: totpFactor.id })
    if (challengeError) return { error: challengeError }
    setMfaFactorId(totpFactor.id)
    setMfaChallengeId(challengeData.id)
    setMfaCode('')
    return { error: null, factor: totpFactor }
  }, [])

  useEffect(() => {
    if (!startInMfa) return
    let cancelled = false
    ;(async () => {
      const { error: challengeError } = await beginChallenge()
      if (!cancelled && challengeError) setError(challengeError.message)
    })()
    return () => { cancelled = true }
  }, [startInMfa, beginChallenge])

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true); setError(''); setSuccess('')
    const { data, error } = await signIn(email, password)
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    // The password has been accepted and a session already exists at this
    // point — it is just not yet at assurance level aal2. App re-checks that
    // level, so handing it the session here is safe; it will keep this screen
    // up until the code is verified.
    const { error: challengeError, factor } = await beginChallenge()
    if (challengeError) {
      setError(challengeError.message)
      setLoading(false)
      return
    }
    if (factor) {
      setMode('mfa')
      setLoading(false)
      return
    }
    onLogin(data.session)
  }

  const handleMFAVerify = async (e) => {
    e.preventDefault()
    if (!mfaCode.trim() || mfaCode.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }
    setLoading(true); setError('')
    try {
      const { data, error } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeId,
        code: mfaCode
      })
      if (error) {
        setError(error.message || 'Invalid code. Please try again.')
        setLoading(false)
        return
      }
      // Verified: the session is now aal2, so App's gate will let it through.
      onLogin(data.session || await supabase.auth.getSession().then(r => r.data.session))
    } catch (err) {
      setError(err.message || 'Verification failed')
      setLoading(false)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true); setError(''); setSuccess('')
    if (!inviteCode.trim()) { setError('Please enter your invitation code.'); setLoading(false); return }
    if (password.length < 10) { setError('Password must be at least 10 characters'); setLoading(false); return }
    if (!/[A-Z]/.test(password)) { setError('Password must include at least one uppercase letter'); setLoading(false); return }
    if (!/[a-z]/.test(password)) { setError('Password must include at least one lowercase letter'); setLoading(false); return }
    if (!/[0-9]/.test(password)) { setError('Password must include at least one number'); setLoading(false); return }
    if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must include at least one special character'); setLoading(false); return }
    const result = await registerOrg(orgName, fullName, email, password, inviteCode.trim())
    if (!result.success) { setError(result.error); setLoading(false); return }
    setSuccess('Account created! Signing you in...')
    // Auto sign-in after registration
    const { data, error } = await signIn(email, password)
    if (error) { setError('Account created but sign-in failed. Please sign in manually.'); setMode('login'); setLoading(false); return }
    onLogin(data.session)
  }

  const inp = (extra = {}) => ({
    width: '100%', padding: '10px 12px', background: 'var(--bg3)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    color: 'var(--text)', fontSize: 14, outline: 'none', ...extra
  })

  const tabStyle = (active) => ({
    flex: 1, padding: '10px 0', textAlign: 'center', fontSize: 14, fontWeight: 600,
    color: active ? '#a855f7' : 'var(--text2)', cursor: 'pointer',
    borderBottom: active ? '2px solid #a855f7' : '2px solid transparent',
    background: 'none', border: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid',
    borderBottomColor: active ? '#a855f7' : 'transparent',
    transition: 'all 0.2s'
  })

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      <div style={{ position: 'fixed', top: -200, right: -200, width: 600, height: 600, background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="fade-in" style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', marginBottom: 16, fontSize: 22, fontWeight: 700, color: '#fff' }}>A</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px' }}>AgencyBase</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>The Operating System for Creator Agencies</p>
        </div>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 32 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
            <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess('') }} style={tabStyle(mode === 'login')}>Sign In</button>
            <button type="button" onClick={() => { setMode('register'); setError(''); setSuccess('') }} style={tabStyle(mode === 'register')}>Register</button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@youragency.com" style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="********" style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#f87171' }}>{error}</div>}
              <button type="submit" disabled={loading} style={{ width: '100%', padding: '11px 0', borderRadius: 'var(--radius)', border: 'none', background: loading ? 'var(--border)' : 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'default' : 'pointer' }}>
                {loading ? 'Signing in...' : 'Sign in ->'}
              </button>
            </form>
          ) : mode === 'mfa' ? (
            <form onSubmit={handleMFAVerify}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Two-Factor Authentication</h2>
                <p style={{ fontSize: 13, color: 'var(--text2)' }}>Enter the 6-digit code from your authenticator app</p>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Authentication Code</label>
                <input
                  type="text"
                  maxLength="6"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  autoFocus
                  style={{ ...inp(), fontSize: 16, letterSpacing: '4px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600 }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                />
              </div>
              {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#f87171' }}>{error}</div>}
              <button type="submit" disabled={loading || mfaCode.length !== 6} style={{ width: '100%', padding: '11px 0', borderRadius: 'var(--radius)', border: 'none', background: (loading || mfaCode.length !== 6) ? 'var(--border)' : 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: (loading || mfaCode.length !== 6) ? 'default' : 'pointer' }}>
                {loading ? 'Verifying...' : 'Verify Code ->'}
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setMfaCode('') }} style={{ width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Back to Login
              </button>
              <button
                type="button"
                onClick={async () => { await signOut(); window.location.reload() }}
                style={{ width: '100%', marginTop: 10, padding: '9px 0', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', fontSize: 12, cursor: 'pointer' }}
              >
                Sign in as someone else
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 20, fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                🔒 New accounts are currently <strong style={{ color: '#a855f7' }}>invitation-only</strong>. Enter your invitation code below to register.
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invitation Code</label>
                <input type="text" value={inviteCode} onChange={e => setInviteCode(e.target.value)} required placeholder="Enter your invitation code" style={inp({ fontFamily: 'monospace', letterSpacing: '0.5px' })} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Agency Name</label>
                <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} required placeholder="Your Agency Inc." style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your Full Name</label>
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required placeholder="Jane Smith" style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@youragency.com" style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min 10 chars, upper + lower + number + special" style={inp()} onFocus={e => e.target.style.borderColor = 'var(--accent)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#f87171' }}>{error}</div>}
              {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#10b981' }}>{success}</div>}
              <button type="submit" disabled={loading} style={{ width: '100%', padding: '11px 0', borderRadius: 'var(--radius)', border: 'none', background: loading ? 'var(--border)' : 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'default' : 'pointer' }}>
                {loading ? 'Creating your account...' : 'Get Started ->'}
              </button>
              <div style={{ marginTop: 20, padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 8, textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0, lineHeight: 1.6 }}>
                  Don't have an invitation code?<br />
                  <a href="mailto:support@agencybase.ai" style={{ color: '#a855f7', textDecoration: 'none', fontWeight: 600 }}>Contact our team</a> and we'll get you set up.
                </p>
                <p style={{ fontSize: 11, color: 'var(--text3)', margin: '6px 0 0', opacity: 0.7 }}>support@agencybase.ai</p>
              </div>
            </form>
          )}
        </div>
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)', marginTop: 20 }}>
          {mode === 'login'
            ? <>Don't have an account? <button onClick={() => { setMode('register'); setError('') }} style={{ color: '#a855f7', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Register your agency</button></>
            : <>Already have an account? <button onClick={() => { setMode('login'); setError('') }} style={{ color: '#a855f7', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Sign in</button></>
          }
        </p>
      </div>
    </div>
  )
}
